import { CheckIcon } from '@heroicons/react/24/outline';
import { providerIcon } from 'appConstants';
import { classNames, returnSubscriptionPeriod } from 'helpers/validation';
import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useGetPaymentsList } from 'store/subscription/actions';
import { PaymentType, SubscriptionPriceType } from 'store/subscription/types';

interface PreviewSubscriptionStepProps {
  chosenPrice: SubscriptionPriceType;
}

export const PreviewSubscriptionStep = ({
  chosenPrice,
}: PreviewSubscriptionStepProps) => {
  const { data: paymentsMethod } = useGetPaymentsList();
  const [defaultPayment, setDefaultPayment] = useState<PaymentType | null>(null);

  useEffect(() => {
    if (paymentsMethod) {
      const defaultMethod = paymentsMethod[0];
      if (defaultMethod) {
        setDefaultPayment(defaultMethod);
      }
    }
  }, [paymentsMethod]);

  const returnFreeTrialDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date;
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between overflow-auto mb-5">
        <div className="flex flex-col space-y-3 p-3 rounded-lg bg-gray-100 w-5/12">
          <div>
            <FormattedMessage id="currentTier" />
          </div>
          <div className="text-gray-500 text-lg">
            <FormattedMessage id="preparation" />
          </div>
          <p className="mt-6 flex items-baseline gap-x-1">
            <span className="text-4xl font-bold tracking-tight text-gray-900">
              <FormattedMessage id="free" />
            </span>
          </p>
        </div>
        <div className="text-lg font-medium">{'>'}</div>
        <div className="flex flex-col space-y-3 p-3 rounded-lg bg-gray-100  w-5/12">
          <div>
            <FormattedMessage id="newTier" />
          </div>
          <div className="text-gray-500 text-lg">
            {returnSubscriptionPeriod(chosenPrice.nickname)}
          </div>
          <p className="mt-6 flex items-baseline gap-x-1">
            <span className="text-4xl font-bold tracking-tight text-gray-900">
              <FormattedNumber
                style="currency"
                currency={chosenPrice.currency.toUpperCase()}
                value={+chosenPrice.unit_amount / 100}
                maximumFractionDigits={0}
              />
            </span>
            <span className="text-sm font-semibold leading-6 text-gray-600">/ month</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-b pb-3 mb-3">
        <div className={classNames('w-full')}>
          <div className="text-lg font-medium">
            <FormattedMessage id="whatHappenedNext" />
          </div>
          <ul className="space-y-2 text-sm leading-6 text-gray-800 mt-2">
            <li className="flex gap-x-3">
              <CheckIcon
                className="h-6 w-5 flex-none text-indigo-600"
                aria-hidden="true"
              />
              <FormattedMessage
                id="preparationCreatedRightAway"
                values={{
                  name: chosenPrice.nickname,
                }}
              />
            </li>
            <li className="flex gap-x-3">
              <CheckIcon
                className="h-6 w-5 flex-none text-indigo-600"
                aria-hidden="true"
              />
              <FormattedMessage
                id="preparationCreatedPrice"
                values={{
                  date: (
                    <FormattedDate
                      value={returnFreeTrialDate()}
                      year="numeric"
                      month="long"
                      day="2-digit"
                    />
                  ),
                  price: (
                    <FormattedNumber
                      style="currency"
                      currency={chosenPrice.currency.toUpperCase()}
                      value={+chosenPrice.unit_amount / 100}
                      maximumFractionDigits={0}
                    />
                  ),
                }}
              />
            </li>
          </ul>
        </div>
        <div className="w-full">
          <div className="text-lg font-medium mb-2">
            <FormattedMessage id="payment" />
          </div>
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start">
              <div className="flex items-center text-base text-gray-600 text-left truncate">
                {providerIcon[
                  defaultPayment?.card.brand.toLocaleLowerCase() as keyof typeof providerIcon
                ]?.baseIcon || null}
                <div className="ml-2 truncate">
                  *** *** *** {defaultPayment?.card.last4}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full text-gray-700 text-sm">
        <FormattedMessage
          id="updatePlanTerms"
          values={{
            tierTitle: returnSubscriptionPeriod(chosenPrice.nickname),
            termsLink: (
              <a
                className="text-primary underline"
                rel="noreferrer"
                target="_blank"
                href="https://www.digital-menu.ai/terms"
              >
                DigitalMenu.AI terms
              </a>
            ),
          }}
        />
      </div>
    </div>
  );
};
